import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DEFAULT_CENTER, MAX_PUBLIC_PIN_ZOOM } from '../../constants'
import { ILocation } from '../../types/graphql'

// Google Maps is the single heaviest third-party on listing pages
// (~1.5 MB transferred, ~136 ms main thread during hydration). The map
// section sits below the hero gallery, so defer both the JS chunk
// (ssr:false) and the actual mount via IntersectionObserver — the runtime
// only loads once the user scrolls near the map section.
const MapWrapper = dynamic(() => import('../Map/MapWrapper'), { ssr: false })

function Location({
  location,
  isBrokerMode
}: {
  location: ILocation
  isBrokerMode: boolean
}) {
  const { t } = useTranslation('details')
  const { coordinates } = location

  // `location.coordinates` is already an obfuscated decoy (~80–200m off the
  // real address) — getStaticProps scrubs the true lat/lng at the data
  // boundary before it ever reaches the client (see the [configId] page).
  // Use it as-is; re-obfuscating here would double-offset the pin and make
  // it diverge from the JSON-LD / meta point.
  const approxCenter = useMemo(() => {
    if (!coordinates) return DEFAULT_CENTER
    return coordinates
  }, [coordinates])

  // IntersectionObserver-gated mount. The container keeps its fixed height
  // (no CLS), and the Google Maps SDK only downloads when the user scrolls
  // within 300px of the map. rootMargin gives enough lead time to hide the
  // SDK load latency on normal scroll speeds.
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldMountMap, setShouldMountMap] = useState(false)

  useEffect(() => {
    if (shouldMountMap) return
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldMountMap(true)
          observer.disconnect()
        }
      },
      { rootMargin: '300px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [shouldMountMap])

  return (
    <section>
      <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
        {t('SECTIONS.WHERE_EYEBROW')}
      </p>
      <h2 className="mt-3 font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
        {t('SECTIONS.APPROX_LOCATION')}
      </h2>

      {/* Map Container — hairline frame, no shadow */}
      <div
        ref={containerRef}
        className="mt-6 h-[280px] overflow-hidden border border-ink-100/15 bg-grey-200 md:h-[400px]"
      >
        {shouldMountMap && (
          <MapWrapper
            deferUntilClick
            markers={[{ ...location, coordinates: approxCenter }]}
            center={approxCenter}
            zoom={14}
            maxZoom={MAX_PUBLIC_PIN_ZOOM}
            white
          />
        )}
      </div>

      <p className="mt-4 max-w-md text-[13px] leading-relaxed text-ink-200 md:text-sm">
        {isBrokerMode
          ? 'Para ver la dirección exacta, contacta al anfitrión.'
          : 'Para ver la dirección exacta, haz una reserva y espera la aprobación del anfitrión.'}
      </p>
    </section>
  )
}

export default React.memo(Location)
